import React, { useState } from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';

import { CheckOutlined, ClearOutlined } from '@mui/icons-material';

import {
  panelSx,
  scrollContainerSx,
  templatesTableSx,
  headerCellSx,
  headerTitleSx,
  addRowSx,
  addInputCellSx,
  inputSx,
  addActionCellSx,
  actionWrapperSx,
  actionIconSx,
  addIconSx,
  clearIconSx,
  actionSvgSx
} from '../Styles/InputStyles';

import TemplateTextRow from './TemplateTextRow';

const CHECKBOX_COLUMN_WIDTH = 52;
const ACTION_COLUMN_WIDTH = 82;

const TemplateTextPanel = ({
  title,
  rows,
  onAddText,
  onToggleText,
  onEditText,
  onReorderText,
  showCheckbox
}) => {
  const [newText, setNewText] = useState('');
  const [editingTextId, setEditingTextId] = useState(null);
  const [editingTextValue, setEditingTextValue] = useState('');
  const [draggedItemId, setDraggedItemId] = useState(null);
  const [dragOverItemId, setDragOverItemId] = useState(null);

  const handleAdd = () => {
    const value = newText.trim();

    if (!value) return;

    onAddText(value);
    setNewText('');
  };

  const handleClear = () => {
    setNewText('');
  };

  const handleStartEdit = item => {
    setEditingTextId(item.id);
    setEditingTextValue(item.text);
  };

  const handleSaveEdit = itemId => {
    const value = editingTextValue.trim();

    if (!value) return;

    onEditText(itemId, value);

    setEditingTextId(null);
    setEditingTextValue('');
  };

  const handleCancelEdit = () => {
    setEditingTextId(null);
    setEditingTextValue('');
  };

  const resetDragState = () => {
    setDraggedItemId(null);
    setDragOverItemId(null);
  };

  const handleDragStart = (event, itemId) => {
    setDraggedItemId(itemId);
    setDragOverItemId(null);

    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(itemId));
  };

  const handleDragOver = (event, itemId) => {
    event.preventDefault();

    event.dataTransfer.dropEffect = 'move';

    if (itemId !== draggedItemId) {
      setDragOverItemId(itemId);
    }
  };

  const handleDrop = (event, targetItemId) => {
    event.preventDefault();

    const transferredId = Number(event.dataTransfer.getData('text/plain'));

    const sourceItemId = draggedItemId ?? transferredId;

    if (!sourceItemId || sourceItemId === targetItemId) {
      resetDragState();
      return;
    }

    const sourceIndex = rows.findIndex(item => item.id === sourceItemId);

    const targetIndex = rows.findIndex(item => item.id === targetItemId);

    if (sourceIndex === -1 || targetIndex === -1) {
      resetDragState();
      return;
    }

    const reorderedRows = [...rows];

    const [movedItem] = reorderedRows.splice(sourceIndex, 1);

    reorderedRows.splice(targetIndex, 0, movedItem);

    onReorderText(reorderedRows);

    resetDragState();
  };

  return (
    <Box
      sx={{
        ...panelSx,
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
      <TableContainer
        sx={{
          ...scrollContainerSx,
          flex: rows.length ? 1 : '0 0 auto',
          minHeight: 0,
          overflowY: rows.length > 0 ? 'auto' : 'hidden'
        }}>
        <Table stickyHeader size="small" sx={templatesTableSx}>
          <colgroup>
            {showCheckbox && (
              <col
                style={{
                  width: CHECKBOX_COLUMN_WIDTH
                }}
              />
            )}

            <col />

            <col
              style={{
                width: ACTION_COLUMN_WIDTH
              }}
            />
          </colgroup>

          <TableHead>
            <TableRow>
              <TableCell colSpan={showCheckbox ? 3 : 2} sx={headerCellSx}>
                <Typography sx={headerTitleSx}>{title}</Typography>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            <TableRow
              sx={{
                ...addRowSx,
                height: '56px !important'
              }}>
              <TableCell
                colSpan={showCheckbox ? 2 : 1}
                sx={{
                  ...addInputCellSx,
                  height: '56px !important',
                  minWidth: 0
                }}>
                <TextField
                  autoComplete="off"
                  fullWidth
                  size="small"
                  placeholder="Add new text"
                  value={newText}
                  onChange={event => setNewText(event.target.value)}
                  onKeyDown={event => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handleAdd();
                    }

                    if (event.key === 'Escape') {
                      handleClear();
                    }
                  }}
                  sx={inputSx}
                />
              </TableCell>

              <TableCell align="center" sx={addActionCellSx}>
                <Box sx={actionWrapperSx}>
                  <IconButton
                    size="small"
                    aria-label="Add template text"
                    disabled={!newText.trim()}
                    onClick={handleAdd}
                    sx={{
                      ...actionIconSx,
                      ...addIconSx
                    }}>
                    <CheckOutlined sx={actionSvgSx} />
                  </IconButton>

                  <IconButton
                    size="small"
                    aria-label="Clear template text"
                    disabled={!newText}
                    onClick={handleClear}
                    sx={{
                      ...actionIconSx,
                      ...clearIconSx
                    }}>
                    <ClearOutlined sx={actionSvgSx} />
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>

            {rows.map(item => (
              <TemplateTextRow
                key={item.id}
                item={item}
                showCheckbox={showCheckbox}
                editing={editingTextId === item.id}
                editingValue={editingTextValue}
                isDragging={draggedItemId === item.id}
                isDragOver={dragOverItemId === item.id}
                onEditingValueChange={setEditingTextValue}
                onToggle={() => onToggleText(item.id)}
                onEdit={() => handleStartEdit(item)}
                onSave={() => handleSaveEdit(item.id)}
                onCancel={handleCancelEdit}
                onDragStart={event => handleDragStart(event, item.id)}
                onDragOver={event => handleDragOver(event, item.id)}
                onDrop={event => handleDrop(event, item.id)}
                onDragEnd={resetDragState}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {!rows.length && (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2
          }}>
          <Typography
            sx={{
              fontSize: 14,
              color: '#757575',
              textAlign: 'center'
            }}>
            No template text available
          </Typography>
        </Box>
      )}
    </Box>
  );
};

TemplateTextPanel.propTypes = {
  title: PropTypes.string,

  rows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      text: PropTypes.string.isRequired,
      checked: PropTypes.bool
    })
  ),

  onAddText: PropTypes.func,
  onToggleText: PropTypes.func,
  onEditText: PropTypes.func,
  onReorderText: PropTypes.func,
  showCheckbox: PropTypes.bool
};

TemplateTextPanel.defaultProps = {
  title: '',
  rows: [],
  onAddText: () => {},
  onToggleText: () => {},
  onEditText: () => {},
  onReorderText: () => {},
  showCheckbox: true
};

export default TemplateTextPanel;
